"use client";

import {
  useState,
  useEffect,
  useRef,
  type FormEvent,
  type ChangeEvent,
} from "react";
import {
  Box,
  Container,
  Flex,
  Heading,
  Text,
  Button,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  RadioGroup,
  Radio,
  VStack,
  HStack,
  Card,
  CardBody,
  Alert,
  AlertIcon,
  Spinner,
  Center,
  Link as ChakraLink,
  Icon,
  Divider,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  Tooltip,
  IconButton,
  Image,
} from "@chakra-ui/react";
import { Select } from "chakra-react-select";
import type { SingleValue } from "react-select";
import { FiArrowLeft, FiFileText, FiSave, FiHelpCircle } from "react-icons/fi";
import { useNavigate, Link } from "react-router-dom";
import { supabase } from "../supabaseClient";
import type { DocumentType, Template } from "../types/types";
import { useAuth } from "../contexts/AuthContext";
import { Link as RouterLink } from "react-router-dom";
import { uploadFilesToS3 } from "../utils/s3Upload";
import type { S3UploadResult } from "../utils/s3Upload";

interface DocumentTemplate {
  id: string;
  document_type_id: number;
  prompt_template: string;
}

const BYTES_200_MB = 200 * 1024 * 1024;

export default function DocumentSubmission() {
  const {
    user,
    profile,
    loading: authLoading,
    session,
    refreshProfile,
  } = useAuth();

  const [documentTypes, setDocumentTypes] = useState<DocumentType[]>([]);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [documentTemplate, setDocumentTemplate] =
    useState<DocumentTemplate | null>(null);
  const [selectedDocType, setSelectedDocType] = useState<number | null>(null);
  const [selectedTemplate, setSelectedTemplate] = useState<string | null>(null);
  const [structuralTemplate, setStructuralTemplate] = useState("");
  const [defaultStructuralTemplate, setDefaultStructuralTemplate] =
    useState("");
  const [prompt, setPrompt] = useState("");
  const [defaultPrompt, setDefaultPrompt] = useState("");
  const [previousPrompts, setPreviousPrompts] = useState<string[]>([]);
  const [medicalRecordsFiles, setMedicalRecordsFiles] = useState<File[]>([]);
  const [letterheadFile, setLetterheadFile] = useState<File | null>(null);
  const [outputFormat, setOutputFormat] = useState<"PDF" | "DOCX">("PDF");
  const [pageLoading, setPageLoading] = useState(true);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [templateModified, setTemplateModified] = useState(false);
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [templateName, setTemplateName] = useState("");
  const [customDocumentName, setCustomDocumentName] = useState("");

  const { isOpen, onOpen, onClose } = useDisclosure();
  const structuralTemplateRef = useRef<HTMLTextAreaElement>(null);
  const navigate = useNavigate();

  // NEW: refs to file inputs so we can sync their FileList with state.
  const medicalRecordsInputRef = useRef<HTMLInputElement>(null);
  const letterheadInputRef = useRef<HTMLInputElement>(null);

  const portalTarget =
    typeof window !== "undefined" ? document.body : undefined;

  useEffect(() => {
    if (authLoading) return;

    async function fetchData() {
      setPageLoading(true);
      setError(null);
      try {
        if (!user) {
          setError("User not authenticated.");
          return;
        }

        const { data: docTypesData, error: docTypesError } = await supabase
          .from("document_types")
          .select("*")
          .order("name");
        if (docTypesError) throw docTypesError;
        setDocumentTypes(docTypesData || []);

        const { data: userTemplates, error: templatesError } = await supabase
          .from("templates")
          .select("*")
          .eq("user_id", user.id)
          .order("name");
        if (templatesError) throw templatesError;
        setTemplates(userTemplates || []);

        const { data: userDocs, error: docsError } = await supabase
          .from("documents")
          .select("prompt")
          .eq("user_id", user.id)
          .order("created_at", { ascending: false })
          .limit(5);
        if (docsError) throw docsError;
        if (userDocs) {
          const uniquePrompts = Array.from(
            new Set(userDocs.map((doc) => doc.prompt).filter((p) => p))
          );
          setPreviousPrompts(uniquePrompts);
        }
      } catch (err: any) {
        setError(`Failed to load initial data: ${err.message}`);
      } finally {
        setPageLoading(false);
      }
    }
    fetchData();
  }, [user, authLoading]);

  // Load default structural template from client directory
  const loadDefaultStructuralTemplate = async (docTypeName: string) => {
    try {
      const response = await fetch(`/documents_templates/${docTypeName}.txt`);
      if (response.ok) {
        const templateContent = await response.text();
        setDefaultStructuralTemplate(templateContent);
        setStructuralTemplate(templateContent);
        setTemplateModified(false);
      } else {
        console.warn(`No default template found for ${docTypeName}`);
        setDefaultStructuralTemplate("");
        setStructuralTemplate("");
      }
    } catch (error) {
      console.error("Error loading default template:", error);
      setDefaultStructuralTemplate("");
      setStructuralTemplate("");
    }
  };

  useEffect(() => {
    if (selectedDocType) {
      // Fetch document template for prompt
      const fetchTemplate = async () => {
        try {
          const { data, error } = await supabase
            .from("document_templates")
            .select("*")
            .eq("document_type_id", selectedDocType)
            .single();

          if (error) {
            console.error("Error fetching document template:", error);
            setDocumentTemplate(null);
            setPrompt("");
            setDefaultPrompt("");
            return;
          }

          if (data) {
            setDocumentTemplate(data);
            const defaultPromptText = data.prompt_template || "";
            setDefaultPrompt(defaultPromptText);
            setPrompt(defaultPromptText);
          }
        } catch (err) {
          console.error("Error loading document template:", err);
          setDocumentTemplate(null);
          setPrompt("");
          setDefaultPrompt("");
        }
      };

      const docType = documentTypes.find((dt) => dt.id === selectedDocType);
      if (docType) {
        if (docType.name === "Miscellaneous") {
          setDefaultStructuralTemplate("");
          setStructuralTemplate("");
        } else {
          loadDefaultStructuralTemplate(docType.name);
        }
      }

      fetchTemplate();
    } else {
      setDocumentTemplate(null);
      setPrompt("");
      setDefaultPrompt("");
      setStructuralTemplate("");
      setDefaultStructuralTemplate("");
      setSelectedTemplate(null);
      setCustomDocumentName("");
      // Also clear file inputs when fully resetting the form
      setMedicalRecordsFiles([]);
      if (medicalRecordsInputRef.current)
        medicalRecordsInputRef.current.value = "";
      setLetterheadFile(null);
      if (letterheadInputRef.current) letterheadInputRef.current.value = "";
    }
  }, [selectedDocType, documentTypes]);

  // selectedTemplate handling
  useEffect(() => {
    if (selectedTemplate) {
      const template = templates.find((t) => t.id === selectedTemplate);
      if (template) {
        if (template.structural_template) {
          setStructuralTemplate(template.structural_template);
          setTemplateModified(false);
        } else if (template.file_path) {
          const loadTemplateFromStorage = async () => {
            try {
              const { data, error } = await supabase.storage
                .from("templates")
                .download(template.file_path);

              if (error) {
                console.error("Error downloading template:", error);
                return;
              }

              const fileExtension = template.file_path
                .toLowerCase()
                .split(".")
                .pop();
              let text = "";

              if (fileExtension === "txt") {
                text = await data.text();
              } else if (
                fileExtension === "pdf" ||
                fileExtension === "docx" ||
                fileExtension === "doc"
              ) {
                const formData = new FormData();
                formData.append(
                  "templateFile",
                  data,
                  template.name || "template"
                );

                try {
                  const response = await fetch(
                    `${
                      process.env.REACT_APP_SERVER_URL || ""
                    }/extract-template-text`,
                    {
                      method: "POST",
                      headers: {
                        Authorization: `Bearer ${session?.access_token}`,
                      },
                      body: formData,
                    }
                  );

                  if (response.ok) {
                    const result = await response.json();
                    text = result.text || "";
                  } else {
                    console.error("Failed to extract text from template file");
                    text =
                      "Error: Could not extract text from this file type. Please use a .txt file for best results.";
                  }
                } catch (extractError) {
                  console.error(
                    "Error extracting text from template:",
                    extractError
                  );
                  text =
                    "Error: Could not extract text from this file type. Please use a .txt file for best results.";
                }
              } else {
                text =
                  "Error: Unsupported file type. Please use .txt, .pdf, or .docx files.";
              }

              setStructuralTemplate(text);
              setTemplateModified(false);
            } catch (error) {
              console.error("Error loading template from storage:", error);
              setStructuralTemplate(
                "Error loading template. Please try again."
              );
            }
          };
          loadTemplateFromStorage();
        }
      }
    } else if (selectedDocType && !selectedTemplate) {
      const docType = documentTypes.find((dt) => dt.id === selectedDocType);
      if (docType && docType.name === "Miscellaneous") {
        setStructuralTemplate("");
        setDefaultStructuralTemplate("");
        setTemplateModified(false);
      } else if (defaultStructuralTemplate) {
        setStructuralTemplate(defaultStructuralTemplate);
        setTemplateModified(false);
      }
    }
  }, [
    selectedTemplate,
    templates,
    defaultStructuralTemplate,
    selectedDocType,
    documentTypes,
    session,
  ]);

  useEffect(() => {
    if (letterheadFile) {
      const fileType = letterheadFile.type;
      if (fileType === "application/pdf") {
        setOutputFormat("PDF");
      } else if (
        fileType ===
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
      ) {
        setOutputFormat("DOCX");
      }
    }
  }, [letterheadFile]);

  const handleStructuralTemplateChange = (
    e: ChangeEvent<HTMLTextAreaElement>
  ) => {
    setStructuralTemplate(e.target.value);
    if (!templateModified && e.target.value !== defaultStructuralTemplate) {
      setTemplateModified(true);
    }
  };

  const handleStructuralTemplateBlur = () => {
    if (templateModified && structuralTemplate !== defaultStructuralTemplate) {
      const shouldSave = window.confirm(
        "You've modified the structure template. Would you like to save it for future use?"
      );
      if (shouldSave) onOpen();
    }
  };

  const handleSaveTemplate = async () => {
    if (!templateName.trim() || !selectedDocType || !user) {
      setError("Please provide a template name");
      return;
    }

    try {
      setSavingTemplate(true);
      setError(null);

      // Preserve original extension; default txt
      let fileExtension = "txt";
      if (selectedTemplate) {
        const originalTemplate = templates.find(
          (t) => t.id === selectedTemplate
        );
        if (originalTemplate?.file_path) {
          const originalExtension = originalTemplate.file_path
            .toLowerCase()
            .split(".")
            .pop();
          if (originalExtension) fileExtension = originalExtension;
        }
      }

      const templateContent = structuralTemplate;
      const fileName = `${user.id}/${Date.now()}-${templateName
        .trim()
        .replace(/[^a-zA-Z0-9]/g, "-")}.${fileExtension}`;

      let contentType = "text/plain";
      if (fileExtension === "pdf") contentType = "application/pdf";
      if (fileExtension === "docx")
        contentType =
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document";

      const { error: uploadError } = await supabase.storage
        .from("templates")
        .upload(fileName, templateContent, {
          contentType,
        });
      if (uploadError) throw new Error(uploadError.message);

      let fontStyles = null;
      let createdByExtraction = false;
      let extractionMetadata = null;

      if (selectedTemplate) {
        const originalTemplate = templates.find(
          (t) => t.id === selectedTemplate
        );
        if (originalTemplate) {
          fontStyles = originalTemplate.font_styles;
          createdByExtraction = originalTemplate.created_by_extraction || false;
          extractionMetadata = originalTemplate.extraction_metadata;
        }
      }

      const { data: template, error: templateError } = await supabase
        .from("templates")
        .insert([
          {
            user_id: user.id,
            document_type_id: selectedDocType,
            name: templateName.trim(),
            file_path: fileName,
            structural_template: templateContent,
            font_styles: fontStyles,
            created_by_extraction: createdByExtraction,
            extraction_metadata: extractionMetadata,
          },
        ])
        .select(`*, document_type:document_type_id(name, description)`)
        .single();

      if (templateError) throw templateError;

      setTemplates([template, ...templates]);
      setTemplateModified(false);
      setTemplateName("");
      onClose();
      alert("Template saved successfully!");
    } catch (error: any) {
      console.error("Error saving template:", error.message);
      setError(`Failed to save template: ${error.message}`);
    } finally {
      setSavingTemplate(false);
    }
  };

  // ---- Files logic ----------------------------------------------------
  // Medical records: up to 10 files; combined size < 200 MB (letterhead/template not included)
  const handleMedicalRecordsChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;
    const filesArray = Array.from(e.target.files);

    setMedicalRecordsFiles(filesArray); // keep files in state for UI
    // keep the native input's FileList as-is; removing happens via the Remove buttons
  };

  // Letterhead: single file; max size 200 MB (independent of medical-records limit)
  const handleLetterheadChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || !e.target.files[0]) {
      setLetterheadFile(null);
      return;
    }
    const file = e.target.files[0];
    setLetterheadFile(file);
  };

  // Helper to rebuild the input's FileList after removing one file
  const syncInputFileList = (files: File[]) => {
    if (!medicalRecordsInputRef.current) return;
    // DataTransfer works in modern browsers to compose a new FileList
    const dt = new DataTransfer();
    files.forEach((f) => dt.items.add(f));
    medicalRecordsInputRef.current.files = dt.files;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    if (
      !selectedDocType ||
      !prompt ||
      medicalRecordsFiles.length === 0 ||
      !user ||
      !profile ||
      !session
    ) {
      setError(
        "Please fill in all required fields and ensure you are logged in."
      );
      return;
    }

    const docType = documentTypes.find((dt) => dt.id === selectedDocType);
    if (docType?.name === "Miscellaneous" && !customDocumentName.trim()) {
      setError(
        "Please provide a custom document name for Miscellaneous document type."
      );
      return;
    }

    const now = new Date();
    const subscriptionEndDate = profile.subscription_period_end
      ? new Date(profile.subscription_period_end)
      : null;
    const isWithinSubscriptionPeriod =
      subscriptionEndDate && now <= subscriptionEndDate;

    const canUseMonthlyCredits =
      (profile.subscription_status === "active" ||
        (profile.subscription_status === "canceled" &&
          isWithinSubscriptionPeriod)) &&
      (profile.subscription_remaining_credits || 0) > 0;

    const canUsePurchasedCredits =
      (profile.purchased_remaining_credits || 0) > 0;

    if (!canUseMonthlyCredits && !canUsePurchasedCredits) {
      let errorMessage = "You have no available credits.";
      if (
        profile.subscription_status === "canceled" &&
        !isWithinSubscriptionPeriod
      ) {
        errorMessage =
          "Your subscription has ended and you have no purchased credits. Please subscribe or buy more credits.";
      } else if (
        profile.subscription_status !== "active" &&
        !isWithinSubscriptionPeriod
      ) {
        errorMessage =
          "You have no active subscription and no purchased credits. Please subscribe or buy more credits.";
      }
      setError(errorMessage);
      return;
    }

    // Defensive checks
    const totalMedicalRecordsSize = medicalRecordsFiles.reduce(
      (sum, file) => sum + file.size,
      0
    );
    const isMedicalRecordsSizeExceeded = totalMedicalRecordsSize > BYTES_200_MB;
    const isMedicalRecordsCountExceeded = medicalRecordsFiles.length > 10;
    const isLetterheadTooLarge = letterheadFile
      ? letterheadFile.size > BYTES_200_MB
      : false;

    if (
      isMedicalRecordsSizeExceeded ||
      isMedicalRecordsCountExceeded ||
      isLetterheadTooLarge
    ) {
      setError(
        "Please fix the file-size/count issues highlighted below before submitting."
      );
      return;
    }

    setSubmitLoading(true);

    try {
      let s3UploadResults: S3UploadResult[] = [];

      try {
        const serverUrl =
          process.env.REACT_APP_SERVER_URL || "http://localhost:8000";
        s3UploadResults = await uploadFilesToS3(
          medicalRecordsFiles,
          user.id,
          serverUrl,
          session!.access_token
        );
      } catch (uploadError: any) {
        console.error("[v0] S3 upload failed:", uploadError);
        throw new Error(
          `Failed to upload medical records to S3: ${uploadError.message}`
        );
      }

      // Create FormData with S3 keys (not the actual files)
      const formData = new FormData();
      formData.append("userId", user.id);
      formData.append("documentTypeId", selectedDocType.toString());
      if (selectedTemplate) formData.append("templateId", selectedTemplate);
      formData.append("prompt", prompt);
      formData.append("structuralTemplate", structuralTemplate);

      if (docType?.name === "Miscellaneous" && customDocumentName.trim()) {
        formData.append("customDocumentName", customDocumentName.trim());
      }

      formData.append("medicalRecordsS3Keys", JSON.stringify(s3UploadResults));

      if (letterheadFile) formData.append("letterheadFile", letterheadFile);
      formData.append("outputFormat", outputFormat);

      void fetch(
        `${process.env.REACT_APP_SERVER_URL || ""}/generate-document-s3`,
        {
          method: "POST",
          headers: { Authorization: `Bearer ${session!.access_token}` },
          body: formData,
        }
      )
        .then(async (res) => {
          await refreshProfile();
          if (!res.ok) {
            const txt = await res.text();
            throw new Error(`Upload failed (${res.status}): ${txt}`);
          }
        })
        .catch((err) => {
          console.error("[v0] Background processing error:", err);
        });

      await new Promise((resolve) => setTimeout(resolve, 10000));
      alert(
        "Processing… redirecting you to your document history. The document will appear there and will be sent to your email as well, when it's ready."
      );
      navigate("/document-history");
      setSubmitLoading(false);
    } catch (err: any) {
      console.error("Error preparing document submission:", err);
      setError(`Failed to submit document: ${err.message}`);
      setSubmitLoading(false);
    }
  };

  if (authLoading || pageLoading) {
    return (
      <Center minH="100vh">
        <Spinner size="xl" color="primary.500" />
        <Text mt={4}>Loading submission form...</Text>
      </Center>
    );
  }

  const totalCredits = profile
    ? (profile.subscription_remaining_credits || 0) +
      (profile.purchased_remaining_credits || 0)
    : 0;
  const now = new Date();
  const subscriptionEndDate = profile?.subscription_period_end
    ? new Date(profile.subscription_period_end)
    : null;
  const isWithinSubscriptionPeriod =
    subscriptionEndDate && now <= subscriptionEndDate;
  const canUseMonthlyCredits =
    profile &&
    (profile.subscription_status === "active" ||
      (profile.subscription_status === "canceled" &&
        isWithinSubscriptionPeriod)) &&
    (profile.subscription_remaining_credits || 0) > 0;
  const canUsePurchasedCredits = profile
    ? (profile.purchased_remaining_credits || 0) > 0
    : false;
  const canSubmitCredits = canUseMonthlyCredits || canUsePurchasedCredits;

  const isOutputFormatLocked = Boolean(
    letterheadFile &&
      (letterheadFile.type === "application/pdf" ||
        letterheadFile.type ===
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document")
  );

  const isMiscellaneousType =
    selectedDocType &&
    documentTypes.find((dt) => dt.id === selectedDocType)?.name ===
      "Miscellaneous";

  const totalMedicalRecordsSize = medicalRecordsFiles.reduce(
    (sum, file) => sum + file.size,
    0
  );
  const isMedicalRecordsSizeExceeded = totalMedicalRecordsSize > BYTES_200_MB;
  const isMedicalRecordsCountExceeded = medicalRecordsFiles.length > 10;
  const letterheadSize = letterheadFile ? letterheadFile.size : 0;
  const isLetterheadTooLarge = letterheadFile
    ? letterheadFile.size > BYTES_200_MB
    : false;

  type DocTypeOption = { value: number; label: string; description?: string };
  type TemplateOption = { value: string; label: string; docTypeId: number };

  const docTypeOptions: DocTypeOption[] = documentTypes.map((dt) => ({
    value: dt.id,
    label: dt.name,
    description: dt.description ?? undefined,
  }));

  const selectedDocTypeOption =
    docTypeOptions.find((o) => o.value === selectedDocType) ?? null;

  const templateOptions: TemplateOption[] = templates
    .filter((t) => !selectedDocType || t.document_type_id === selectedDocType)
    .map((t) => ({
      value: t.id,
      label: t.name,
      docTypeId: t.document_type_id,
    }));

  const selectedTemplateOption =
    templateOptions.find((o) => o.value === selectedTemplate) ?? null;

  const handleDocTypeSelect = (opt: SingleValue<DocTypeOption>) => {
    const docTypeId = opt?.value ?? null;
    setSelectedDocType(docTypeId);
    setSelectedTemplate(null);
    setStructuralTemplate("");
    setTemplateModified(false);
    setCustomDocumentName("");

    // Also clear file inputs when switching doc type
    setMedicalRecordsFiles([]);
    if (medicalRecordsInputRef.current)
      medicalRecordsInputRef.current.value = "";
    setLetterheadFile(null);
    if (letterheadInputRef.current) letterheadInputRef.current.value = "";
  };

  const handleTemplateSelect = (opt: SingleValue<TemplateOption>) => {
    const templateId = opt?.value ?? null;
    setSelectedTemplate(templateId);
    setTemplateModified(false);
  };

  return (
    <Box minH="100vh" bg="gray.50">
      {/* Header */}
      <Box
        bg="rgb(253, 251, 252)"
        shadow="sm"
        position="sticky"
        top={0}
        zIndex={10}
      >
        <Container maxW="4xl">
          <Flex h={16} align="center" justify="space-between">
            <Image
              src="/main-logo.png"
              alt="RadiusDocs AI"
              height="50px"
              width="auto"
              objectFit="contain"
            />
            <Button
              onClick={() => navigate("/dashboard")}
              variant="ghost"
              leftIcon={<Icon as={FiArrowLeft} />}
              colorScheme="primary"
            >
              Back to Dashboard
            </Button>
          </Flex>
        </Container>
      </Box>

      <Container maxW="4xl" py={8}>
        <Card>
          <CardBody p={8}>
            <VStack spacing={6}>
              <Heading size="lg" color="gray.700">
                Create New Document
              </Heading>

              {error && (
                <Alert status="error" borderRadius="md">
                  <AlertIcon />
                  {error}
                </Alert>
              )}

              {profile && (
                <Alert status="info" borderRadius="md">
                  <AlertIcon />
                  <VStack align="start" spacing={1}>
                    <Text>
                      <strong>Available Credits:</strong> {totalCredits}
                    </Text>
                    <Text>
                      <strong>Subscription:</strong>{" "}
                      {profile.subscription_status || "None"}
                    </Text>
                    {!canSubmitCredits && (
                      <Text color="red.600">
                        You need an active subscription with credits or
                        purchased credits to submit documents.{" "}
                        <ChakraLink
                          as={Link}
                          to="/billing"
                          color="primary.600"
                          fontWeight="bold"
                        >
                          Manage Billing
                        </ChakraLink>
                        .
                      </Text>
                    )}
                  </VStack>
                </Alert>
              )}

              <Box as="form" onSubmit={handleSubmit} w="100%">
                <VStack spacing={6}>
                  <FormControl isRequired>
                    <HStack spacing={2} align="center">
                      <FormLabel mb={0}>Document Type</FormLabel>
                      <Tooltip
                        label="Select the type of medical document you want to generate. Each type has specific formatting and content requirements."
                        placement="top"
                        hasArrow
                        backgroundColor={"primary.500"}
                        backdropBlur={"md"}
                      >
                        <IconButton
                          aria-label="Document type help"
                          icon={<FiHelpCircle />}
                          size="xs"
                          variant="ghost"
                          color="brand.500"
                        />
                      </Tooltip>
                    </HStack>
                    <Select<DocTypeOption, false>
                      placeholder="Select Document Type"
                      options={docTypeOptions}
                      value={selectedDocTypeOption}
                      onChange={handleDocTypeSelect}
                      isDisabled={submitLoading}
                      menuPortalTarget={portalTarget as HTMLElement}
                      menuPosition="fixed"
                      menuPlacement="auto"
                      menuShouldScrollIntoView={false}
                      chakraStyles={{
                        control: (base) => ({
                          ...base,
                          bg: "gray.50",
                          borderColor: "brand.200",
                          _hover: { borderColor: "brand.300" },
                          _focusWithin: {
                            borderColor: "brand.500",
                            boxShadow:
                              "0 0 0 1px var(--chakra-colors-brand-500)",
                          },
                        }),
                        dropdownIndicator: (base) => ({
                          ...base,
                          color: "brand.500",
                        }),
                        menu: (base) => ({
                          ...base,
                          zIndex: 2000,
                          bg: "white",
                        }),
                        option: (base, state) => ({
                          ...base,
                          bg: state.isSelected
                            ? "brand.500"
                            : state.isFocused
                            ? "brand.100"
                            : "white",
                          color: state.isSelected ? "white" : "gray.700",
                          _active: { bg: "brand.600", color: "white" },
                        }),
                      }}
                      styles={{
                        menuPortal: (base) => ({ ...base, zIndex: 2000 }),
                      }}
                    />
                    {selectedDocType && (
                      <Text fontSize="sm" color="gray.600" mt={2}>
                        {
                          documentTypes.find((dt) => dt.id === selectedDocType)
                            ?.description
                        }
                      </Text>
                    )}
                  </FormControl>

                  {/* Custom Document Name for Miscellaneous Type */}
                  {isMiscellaneousType && (
                    <FormControl isRequired>
                      <HStack spacing={2} align="center">
                        <FormLabel mb={0}>Custom Document Name</FormLabel>
                        <Tooltip
                          label="Provide a specific name for your document (e.g., 'Demand Letter', 'Cost Estimate', 'Medical Summary'). This will be used as the document type label."
                          placement="top"
                          hasArrow
                          backgroundColor={"primary.500"}
                          backdropBlur={"md"}
                        >
                          <IconButton
                            aria-label="Custom document name help"
                            icon={<FiHelpCircle />}
                            size="xs"
                            variant="ghost"
                            color="brand.500"
                          />
                        </Tooltip>
                      </HStack>
                      <Input
                        placeholder="e.g., Demand Letter, Cost Estimate, Medical Summary"
                        value={customDocumentName}
                        onChange={(e) => setCustomDocumentName(e.target.value)}
                        isDisabled={submitLoading}
                        bg="gray.50"
                      />
                      <Text fontSize="xs" color="gray.600" mt={1}>
                        This name will be displayed as the document type in your
                        document history and generated files.
                      </Text>
                    </FormControl>
                  )}

                  {/* Report Structure Template */}
                  <FormControl>
                    <HStack spacing={2} align="center">
                      <FormLabel mb={0}>Report Structure Template</FormLabel>
                      <Tooltip
                        label="Choose a custom template you've saved previously, or use the default template for the selected document type. Templates define the structure and sections of your document."
                        placement="top"
                        hasArrow
                        backgroundColor={"primary.500"}
                        backdropBlur={"md"}
                      >
                        <IconButton
                          aria-label="Template help"
                          icon={<FiHelpCircle />}
                          size="xs"
                          variant="ghost"
                          color="brand.500"
                        />
                      </Tooltip>
                    </HStack>

                    {isMiscellaneousType && (
                      <Alert status="info" mb={4} borderRadius="md">
                        <AlertIcon />
                        <VStack align="start" spacing={1}>
                          <Text fontSize="sm" fontWeight="medium">
                            Miscellaneous Document Type Selected
                          </Text>
                          <Text fontSize="sm">
                            For this document type, you must upload your own
                            template or select a previously saved template. The
                            system will use your template as a formatting guide
                            while generating content based on your medical
                            records.
                          </Text>
                        </VStack>
                      </Alert>
                    )}

                    <Select<TemplateOption, false>
                      placeholder="Select Structure Template"
                      options={templateOptions}
                      value={selectedTemplateOption}
                      onChange={handleTemplateSelect}
                      isDisabled={submitLoading || templateOptions.length === 0}
                      menuPortalTarget={portalTarget as HTMLElement}
                      menuPosition="fixed"
                      menuPlacement="auto"
                      menuShouldScrollIntoView={false}
                      chakraStyles={{
                        control: (base) => ({
                          ...base,
                          bg: "gray.50",
                          borderColor: "brand.200",
                          _hover: { borderColor: "brand.300" },
                          _focusWithin: {
                            borderColor: "brand.500",
                            boxShadow:
                              "0 0 0 1px var(--chakra-colors-brand-500)",
                          },
                        }),
                        dropdownIndicator: (base) => ({
                          ...base,
                          color: "brand.500",
                        }),
                        menu: (base) => ({
                          ...base,
                          zIndex: 2000,
                          bg: "white",
                        }),
                        option: (base, state) => ({
                          ...base,
                          bg: state.isSelected
                            ? "brand.500"
                            : state.isFocused
                            ? "brand.100"
                            : "white",
                          color: state.isSelected ? "white" : "gray.700",
                          _active: { bg: "brand.600", color: "white" },
                        }),
                      }}
                      styles={{
                        menuPortal: (base) => ({ ...base, zIndex: 2000 }),
                      }}
                    />

                    {templates.length === 0 && (
                      <Text fontSize="sm" color="gray.600" mt={2}>
                        No custom templates available. You can create and save
                        custom templates by modifying the structure below.
                      </Text>
                    )}

                    {isMiscellaneousType && !selectedTemplate && (
                      <Alert status="warning" mt={2} borderRadius="md">
                        <AlertIcon />
                        <Text fontSize="sm">
                          Please select a template or upload a new one using{" "}
                          <ChakraLink
                            as={RouterLink}
                            to="/templates"
                            color="teal.500"
                            textDecoration="underline"
                            fontWeight="semibold"
                          >
                            Templates
                          </ChakraLink>{" "}
                          section. Miscellaneous documents require a custom
                          template to define the structure.
                        </Text>
                      </Alert>
                    )}

                    {structuralTemplate && (
                      <Box mt={4}>
                        <Flex justify="space-between" align="center" mb={2}>
                          <HStack spacing={2} align="center">
                            <Text
                              fontSize="sm"
                              fontWeight="medium"
                              color="gray.700"
                            >
                              Document Structure (Editable):
                            </Text>
                            <Tooltip
                              label="This template defines the structure and sections of your document. You can edit it to customize the format. Use **text** to make text bold in the final document."
                              placement="top"
                              hasArrow
                              backgroundColor={"primary.500"}
                              backdropBlur={"md"}
                            >
                              <IconButton
                                aria-label="Structure template help"
                                icon={<FiHelpCircle />}
                                size="xs"
                                variant="ghost"
                                color="brand.500"
                              />
                            </Tooltip>
                          </HStack>
                          {templateModified && (
                            <HStack spacing={2}>
                              <Text fontSize="xs" color="orange.600">
                                Modified
                              </Text>
                              <Button
                                size="xs"
                                colorScheme="primary"
                                variant="outline"
                                leftIcon={<Icon as={FiSave} />}
                                onClick={onOpen}
                              >
                                Save Template
                              </Button>
                            </HStack>
                          )}
                        </Flex>
                        <Textarea
                          ref={structuralTemplateRef}
                          value={structuralTemplate}
                          onChange={handleStructuralTemplateChange}
                          onBlur={handleStructuralTemplateBlur}
                          isDisabled={submitLoading}
                          rows={15}
                          bg="gray.50"
                          fontSize="sm"
                          fontFamily="monospace"
                          placeholder="Document structure will appear here when you select a document type..."
                          sx={{
                            scrollbarWidth: "thin",
                            scrollbarColor: "white white",
                          }}
                        />
                        <Text fontSize="xs" color="gray.600" mt={1}>
                          This template defines the structure of your document.
                          You can edit it and save custom versions for future
                          use. Use **text** to make text bold in the final
                          document.
                        </Text>
                      </Box>
                    )}
                  </FormControl>

                  <Divider />

                  <FormControl isRequired>
                    <HStack spacing={2} align="center">
                      <FormLabel mb={0}>Prompt / Instructions</FormLabel>
                      <Tooltip
                        label="Provide specific instructions for the AI about tone, writing style, and any special requirements. Be as detailed as possible for better results."
                        placement="top"
                        hasArrow
                        backgroundColor={"primary.500"}
                        backdropBlur={"md"}
                      >
                        <IconButton
                          aria-label="Prompt help"
                          icon={<FiHelpCircle />}
                          size="xs"
                          variant="ghost"
                          color="brand.500"
                        />
                      </Tooltip>
                    </HStack>
                    <Textarea
                      placeholder="Provide detailed instructions for tone, writing style, and specific requirements..."
                      value={prompt}
                      onChange={(e) => setPrompt(e.target.value)}
                      isDisabled={submitLoading}
                      rows={5}
                      bg="gray.50"
                      variant="outline"
                      borderColor="brand.200"
                      _hover={{ borderColor: "brand.300" }}
                      _focus={{
                        borderColor: "brand.500",
                        boxShadow: "0 0 0 1px var(--chakra-colors-brand-500)",
                      }}
                    />
                    <Text fontSize="xs" color="gray.600" mt={1}>
                      Specify the tone, writing style, and any specific
                      requirements for your document.
                      {defaultPrompt && (
                        <Button
                          variant="link"
                          size="xs"
                          color="primary.600"
                          onClick={() => setPrompt(defaultPrompt)}
                          ml={2}
                        >
                          Reset to default
                        </Button>
                      )}
                    </Text>
                    {previousPrompts.length > 0 && (
                      <VStack align="start" mt={3} spacing={2}>
                        <Text fontSize="sm" color="gray.600">
                          Previous prompts:
                        </Text>
                        <VStack spacing={2} w="100%">
                          {previousPrompts.map((prevPrompt, index) => (
                            <Button
                              key={index}
                              variant="outline"
                              size="sm"
                              onClick={() => setPrompt(prevPrompt)}
                              isDisabled={submitLoading}
                              w="100%"
                              textAlign="left"
                              justifyContent="flex-start"
                              h="auto"
                              py={2}
                              whiteSpace="normal"
                              colorScheme="primary"
                            >
                              {prevPrompt.length > 50
                                ? `${prevPrompt.substring(0, 50)}...`
                                : prevPrompt}
                            </Button>
                          ))}
                        </VStack>
                      </VStack>
                    )}
                  </FormControl>

                  <Divider />

                  {/* Letterhead (single, independent 200MB cap) */}
                  <FormControl>
                    <HStack spacing={2} align="center">
                      <FormLabel mb={0}>Letterhead (Optional)</FormLabel>
                      <Tooltip
                        label="Upload your practice letterhead. Max 200MB. This does NOT count toward the 200MB medical-records limit."
                        placement="top"
                        hasArrow
                        backgroundColor={"primary.500"}
                        backdropBlur={"md"}
                      >
                        <IconButton
                          aria-label="Letterhead help"
                          icon={<FiHelpCircle />}
                          size="xs"
                          variant="ghost"
                          color="brand.500"
                        />
                      </Tooltip>
                    </HStack>
                    <Box
                      position="relative"
                      borderWidth="1px"
                      borderRadius="md"
                      borderColor="gray.200"
                      p={1}
                      bg="gray.50"
                    >
                      <Input
                        ref={letterheadInputRef}
                        type="file"
                        onChange={handleLetterheadChange}
                        isDisabled={submitLoading}
                        accept=".pdf,.docx,.jpg,.jpeg,.png,.gif,.bmp,.tiff,.webp"
                        height="auto"
                        py={1}
                        px={2}
                        variant="outline"
                        borderColor="transparent"
                        _hover={{ borderColor: "transparent" }}
                        outline="none"
                        boxShadow="none"
                        _focus={{
                          outline: "none",
                          boxShadow: "none",
                          borderColor: "transparent",
                        }}
                        _focusVisible={{
                          outline: "none",
                          boxShadow: "none",
                          borderColor: "transparent",
                        }}
                        sx={{
                          "&[data-focus]": {
                            boxShadow: "none",
                            borderColor: "transparent",
                          },
                        }}
                      />
                    </Box>
                    <Text fontSize="xs" color="gray.600" mt={1}>
                      Accepted: PDF, DOCX, JPG, PNG, GIF, BMP, TIFF, WEBP (Max
                      200MB; not part of the medical-records cap)
                    </Text>

                    {letterheadFile && letterheadFile.size > BYTES_200_MB && (
                      <Alert status="error" mt={2} borderRadius="md">
                        <AlertIcon />
                        <Text fontSize="sm">
                          Letterhead file size (
                          {(letterheadSize / 1024 / 1024).toFixed(2)} MB)
                          exceeds 200MB. Please upload a smaller file.
                        </Text>
                      </Alert>
                    )}

                    {letterheadFile && (
                      <HStack mt={2} spacing={2}>
                        <Text fontSize="sm" color="gray.600">
                          Selected: {letterheadFile.name} (
                          {(letterheadSize / 1024 / 1024).toFixed(2)} MB)
                        </Text>
                        <Button
                          size="xs"
                          variant="ghost"
                          colorScheme="red"
                          onClick={() => {
                            setLetterheadFile(null);
                            if (letterheadInputRef.current)
                              letterheadInputRef.current.value = "";
                          }}
                          isDisabled={submitLoading}
                        >
                          Remove
                        </Button>
                      </HStack>
                    )}
                  </FormControl>

                  <Divider />

                  {/* Medical Records (<=10, combined <200MB) */}
                  <FormControl>
                    <HStack spacing={2} align="center">
                      <FormLabel mb={0}>Medical Records</FormLabel>
                      <Tooltip
                        label="Upload up to 10 files. Combined size of medical records must be less than 200MB. (Letterhead/template not included.)"
                        placement="top"
                        hasArrow
                        backgroundColor={"primary.500"}
                        backdropBlur={"md"}
                      >
                        <IconButton
                          aria-label="Medical records help"
                          icon={<FiHelpCircle />}
                          size="xs"
                          variant="ghost"
                          color="brand.500"
                        />
                      </Tooltip>
                    </HStack>
                    <Box
                      position="relative"
                      borderWidth="1px"
                      borderRadius="md"
                      borderColor="gray.200"
                      p={1}
                      bg="gray.50"
                    >
                      <Input
                        ref={medicalRecordsInputRef}
                        type="file"
                        onChange={handleMedicalRecordsChange}
                        isDisabled={submitLoading}
                        accept=".pdf,.docx"
                        height="auto"
                        py={1}
                        px={2}
                        multiple
                        variant="outline"
                        borderColor="transparent"
                        _hover={{ borderColor: "transparent" }}
                        outline="none"
                        boxShadow="none"
                        _focus={{
                          outline: "none",
                          boxShadow: "none",
                          borderColor: "transparent",
                        }}
                        _focusVisible={{
                          outline: "none",
                          boxShadow: "none",
                          borderColor: "transparent",
                        }}
                        sx={{
                          "&[data-focus]": {
                            boxShadow: "none",
                            borderColor: "transparent",
                          },
                        }}
                      />
                    </Box>
                    <Text fontSize="xs" color="gray.600" mt={1}>
                      Accepted: PDF, DOCX (Max 10 files, combined medical
                      records size &lt; 200MB)
                    </Text>

                    {isMedicalRecordsCountExceeded && (
                      <Alert status="error" mt={2} borderRadius="md">
                        <AlertIcon />
                        <Text fontSize="sm">
                          You selected {medicalRecordsFiles.length} files.
                          Maximum allowed is 10. Please remove some files.
                        </Text>
                      </Alert>
                    )}

                    {isMedicalRecordsSizeExceeded && (
                      <Alert status="error" mt={2} borderRadius="md">
                        <AlertIcon />
                        <Text fontSize="sm">
                          The total combined size of medical records (
                          {(totalMedicalRecordsSize / 1024 / 1024).toFixed(2)}{" "}
                          MB) exceeds the 200MB limit. Please remove some files
                          or upload smaller files.
                        </Text>
                      </Alert>
                    )}

                    {medicalRecordsFiles.length > 0 && (
                      <VStack align="start" mt={2} spacing={1}>
                        <Text
                          fontSize="sm"
                          fontWeight="medium"
                          color="gray.700"
                        >
                          Selected files ({medicalRecordsFiles.length}):
                        </Text>
                        {medicalRecordsFiles.map((file, index) => (
                          <HStack key={index} spacing={2}>
                            <Text fontSize="sm" color="gray.600">
                              {file.name} (
                              {(file.size / 1024 / 1024).toFixed(2)} MB)
                            </Text>
                            <Button
                              size="xs"
                              variant="ghost"
                              colorScheme="red"
                              onClick={() => {
                                const newFiles = medicalRecordsFiles.filter(
                                  (_, i) => i !== index
                                );
                                setMedicalRecordsFiles(newFiles);

                                // 🔁 keep native file input in sync so the small
                                // count label on the right updates immediately
                                syncInputFileList(newFiles);
                              }}
                              isDisabled={submitLoading}
                            >
                              Remove
                            </Button>
                          </HStack>
                        ))}
                        <Text
                          fontSize="xs"
                          color={
                            isMedicalRecordsSizeExceeded
                              ? "red.600"
                              : "gray.600"
                          }
                          fontWeight={
                            isMedicalRecordsSizeExceeded ? "bold" : "normal"
                          }
                          mt={1}
                        >
                          Total medical records size:{" "}
                          {(totalMedicalRecordsSize / 1024 / 1024).toFixed(2)}{" "}
                          MB / 200 MB
                          {isMedicalRecordsSizeExceeded && " - EXCEEDS LIMIT"}
                        </Text>

                        {/* Optional "Clear all" for convenience */}
                        <Button
                          size="xs"
                          variant="ghost"
                          onClick={() => {
                            setMedicalRecordsFiles([]);
                            if (medicalRecordsInputRef.current) {
                              // clear input + FileList
                              medicalRecordsInputRef.current.value = "";
                            }
                          }}
                          isDisabled={submitLoading}
                        >
                          Clear all
                        </Button>
                      </VStack>
                    )}
                  </FormControl>

                  <FormControl>
                    <HStack spacing={2} align="center">
                      <FormLabel mb={0}>Output Format</FormLabel>
                      <Tooltip
                        label="Choose the format for your generated document. This will be locked based on the letterhead file type (PDF letterhead locks to PDF output, Word to DOCX)."
                        placement="top"
                        hasArrow
                        backgroundColor={"primary.500"}
                        backdropBlur={"md"}
                      >
                        <IconButton
                          aria-label="Output format help"
                          icon={<FiHelpCircle />}
                          size="xs"
                          variant="ghost"
                          color="brand.500"
                        />
                      </Tooltip>
                    </HStack>
                    {isOutputFormatLocked && letterheadFile && (
                      <Alert
                        status="info"
                        mb={2}
                        borderRadius="md"
                        fontSize="sm"
                      >
                        <AlertIcon />
                        Output format is locked to {outputFormat} because you
                        uploaded a{" "}
                        {letterheadFile.type.includes("pdf") ? "PDF" : "Word"}{" "}
                        letterhead.
                      </Alert>
                    )}
                    <RadioGroup
                      value={outputFormat}
                      onChange={(value) =>
                        setOutputFormat(value as "PDF" | "DOCX")
                      }
                      isDisabled={submitLoading || isOutputFormatLocked}
                    >
                      <HStack spacing={6}>
                        {["PDF", "DOCX"].map((fmt) => (
                          <Radio
                            key={fmt}
                            value={fmt}
                            colorScheme="brand"
                            bg="white"
                            color="black"
                            borderWidth="2px"
                            borderColor="brand.300"
                            borderRadius="md"
                            px={3}
                            py={1}
                            _checked={{
                              bg: "brand.500",
                              color: "white",
                              borderColor: "brand.500",
                            }}
                            _hover={{ bg: "brand.100" }}
                            _focusVisible={{
                              boxShadow:
                                "0 0 0 2px var(--chakra-colors-brand-300)",
                            }}
                          >
                            {fmt}
                          </Radio>
                        ))}
                      </HStack>
                    </RadioGroup>
                  </FormControl>

                  <HStack spacing={4} w="100%">
                    <Button
                      variant="ghost"
                      onClick={() => navigate("/dashboard")}
                      isDisabled={submitLoading}
                      flex={1}
                      colorScheme="gray"
                      _hover={{ bg: "gray.100" }}
                      color={"gray.600"}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      colorScheme="primary"
                      isLoading={submitLoading}
                      loadingText="Processing..."
                      isDisabled={
                        !canSubmitCredits ||
                        isMedicalRecordsSizeExceeded ||
                        isMedicalRecordsCountExceeded ||
                        isLetterheadTooLarge
                      }
                      leftIcon={<Icon as={FiFileText} />}
                      flex={1}
                    >
                      Submit Document (Uses 1 Credit)
                    </Button>
                  </HStack>
                </VStack>
              </Box>
            </VStack>
          </CardBody>
        </Card>
      </Container>

      {/* Save Template Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="md">
        <ModalOverlay />
        <ModalContent backgroundColor={"gray.50"}>
          <ModalHeader>Save Custom Template</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <Text fontSize="sm" color="gray.600">
                Give your custom template a name to save it for future use.
              </Text>
              <FormControl isRequired>
                <FormLabel>Template Name</FormLabel>
                <Input
                  value={templateName}
                  onChange={(e) => setTemplateName(e.target.value)}
                  placeholder="Enter template name"
                  isDisabled={savingTemplate}
                  bg="white"
                  _placeholder={{ color: "gray.400" }}
                />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button
              variant="ghost"
              mr={3}
              onClick={() => {
                onClose();
                setTemplateName("");
              }}
              isDisabled={savingTemplate}
              colorScheme="gray"
              _hover={{ bg: "gray.100" }}
              color={"gray.600"}
            >
              Cancel
            </Button>
            <Button
              colorScheme="primary"
              onClick={handleSaveTemplate}
              isLoading={savingTemplate}
              loadingText="Saving..."
              leftIcon={<Icon as={FiSave} />}
            >
              Save Template
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}
